import React, { useEffect, useState } from "react";
import {
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllTeamsQuery,
  useGetDepartmentWiseTATReportQuery,
  useLazyGetIssueTypeWiseTATReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isCurrentDateRange = (from: string, to: string) => {
  const today = getTodayDateString();
  return from === today && to === today;
};

type ReportMode = "live" | "historical" | null;

const DepartmentWiseTatReport = () => {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [params, setParams] = useState<any>(null);
  const [selectedTeams, setSelectedTeams] = useState<any>([]);
  const [selectedTeamsParam, setSelectedTeamsParam] = useState<string[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [localLoading, setLocalLoading] = useState(false);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const { data: teamsData, isLoading: teamsLoading } = useGetAllTeamsQuery({});
  const [reportFetching, setReportFetching] = useState(false);
  const [reportMode, setReportMode] = useState<ReportMode>(null);
  const [totalRowsFromApi, setTotalRowsFromApi] = useState<number | null>(null);

  const {
    data: liveDepartmentTatReport,
    isFetching: liveReportFetching,
    status: liveReportStatus,
  } = useGetDepartmentWiseTATReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [triggerDepartmentTatDownload, { isFetching: downloadLoading }] =
    useLazyGetIssueTypeWiseTATReportDownloadQuery();

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  const [theTeams, setTheTeams] = useState<any>([]);
  useEffect(() => {
    const teams = teamsData?.teams;
    if (Array.isArray(teams)) {
      setTheTeams(teams);
    } else {
      setTheTeams([]);
    }
  }, [teamsData]);

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setDataRows([]);
        setTotalRowsFromApi(null);
      }
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      from_date: params.gte,
      to_date: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      report_type: "team_level",
      download: false,
    };
    if (params.team_ids != null && params.team_ids !== "") {
      queryParams.team_ids = typeof params.team_ids === "string" ? params.team_ids : (Array.isArray(params.team_ids) ? params.team_ids.join(",") : String(params.team_ids));
    }

    axios
      .get(`${reportingBaseUrl}/ticket/sla-reports`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const totalRows = data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        setTotalRowsFromApi(typeof totalRows === "number" ? totalRows : results.length);
        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize = data?.page_size ?? params.page_size ?? 20;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });
        const rowOffset = (apiPage - 1) * pageSizeNum;
        const transformedRows = results.map((item: any, index: number) => ({
          ...item,
          id: rowOffset + index + 1,
          closed_percent: item.closed_percent != null ? `${item.closed_percent}%` : "-",
          pending_percent: item.pending_percent != null ? `${item.pending_percent}%` : "-",
          closed_within_sla_percent: item.closed_within_sla_percent != null ? `${item.closed_within_sla_percent}%` : "-",
          closed_with_sla_breach_percent: item.closed_with_sla_breach_percent != null ? `${item.closed_with_sla_breach_percent}%` : "-",
          pending_within_sla_percent: item.pending_within_sla_percent != null ? `${item.pending_within_sla_percent}%` : "-",
          pending_with_sla_breach_percent: item.pending_with_sla_breach_percent != null ? `${item.pending_with_sla_breach_percent}%` : "-",
        }));
        setDataRows(transformedRows);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(err?.response?.data?.message || "Failed to fetch TAT By Department report");
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (params) setLocalLoading(true);
  }, [params, reportMode]);

  useEffect(() => {
    if (reportMode !== "live") return;
    if (!Array.isArray(liveDepartmentTatReport)) {
      setDataRows([]);
      setLocalLoading(false);
      return;
    }
    const transformedRows = liveDepartmentTatReport.map(
      (item: any, index: number) => ({
        ...item,
        id: index + 1,
        closed_percent: `${item.closed_percent || "-"}`,
        pending_percent: `${item.pending_percent || "-"}`,
        closed_within_sla_percent: `${item.closed_within_sla_percent || "-"}`,
        closed_with_sla_breach_percent: `${
          item.closed_with_sla_breach_percent || "-"
        }`,
        pending_within_sla_percent: `${
          item.pending_within_sla_percent || "-"
        }`,
        pending_with_sla_breach_percent: `${
          item.pending_with_sla_breach_percent || "-"
        }`,
      })
    );
    setDataRows(transformedRows);
    setLocalLoading(false);
  }, [liveDepartmentTatReport, reportMode]);

  useEffect(() => {
    const dropdownHeadings = document.querySelectorAll(".dropdown-heading");
    dropdownHeadings.forEach((heading: any) => {
      heading.style.height = "50px";
    });
  }, []);

  const columns: GridColDef[] = [
    {
      field: "team_name",
      headerName: "Department",
      flex: 1,
      minWidth: 150, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "total_tickets",
      headerName: "Total tickets",
      flex: 1,
      minWidth: 150, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "closed_tickets",
      headerName: "Total closed",
      flex: 1,
      minWidth: 150, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "active_tickets",
      headerName: "Total active",
      flex: 1,
      minWidth: 150, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "pending_tickets",
      headerName: "Total pending",
      flex: 1,
      minWidth: 150, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "closed_percent",
      headerName: "Closed%",
      flex: 1,
      minWidth: 120, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "pending_percent",
      headerName: "Pending%",
      flex: 1,
      minWidth: 120, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "closed_within_sla",
      headerName: "Closed within sl",
      flex: 1,
      minWidth: 180, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "closed_with_sla_breach",
      headerName: "Closed sl breach",
      flex: 1,
      minWidth: 180, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "pending_within_sla",
      headerName: "Pending within sl",
      flex: 1,
      minWidth: 180, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "pending_with_sla_breach",
      headerName: "Pending sl breach",
      flex: 1,
      minWidth: 180, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "closed_within_sla_percent",
      headerName: "Closed within sl(%)",
      flex: 1,
      minWidth: 200, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "closed_with_sla_breach_percent",
      headerName: "Closed sl breach(%)",
      flex: 1,
      minWidth: 200, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "pending_within_sla_percent",
      headerName: "Pending within sl(%)",
      flex: 1,
      minWidth: 200, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
    {
      field: "pending_with_sla_breach_percent",
      headerName: "Pending sl breach(%)",
      flex: 1,
      minWidth: 200, // Apply minWidth
      align: "center",
      headerAlign: "center",
    },
  ];
  
  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };

  const handleSubmit = () => {
    const filteredTeams = selectedTeamsParam.filter((team) => team !== "all");

    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }
    if (!filteredTeams.length) {
      toast.error('Please select "Team"');
      return;
    }

    const isLive = isCurrentDateRange(dateFrom, dateTo);
    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
    };

    if (isLive) {
      if (filteredTeams.length > 1) {
        queryParams.team_ids = filteredTeams.join(",");
      } else if (filteredTeams.length === 1) {
        queryParams.team_id = filteredTeams;
      }
    } else {
      queryParams.page = 1;
      queryParams.page_size = paginationModel.pageSize;
      if (filteredTeams.length > 1) {
        queryParams.team_ids = filteredTeams.join(",");
      } else if (filteredTeams.length === 1) {
        queryParams.team_ids = String(filteredTeams[0]);
      }
    }

    setReportMode(isLive ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setParams(queryParams);
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setReportMode(null);
    setSelectedTeams([]);
    setDataRows([]);
    setTotalRowsFromApi(null);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params || reportMode === "live") return;
    setParams({
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    });
  };

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    const downloadPayload: Record<string, string | number> = {
      from_date: params.gte,
      to_date: params.lte,
      page: 1,
      page_size: totalRowsFromApi ?? 99999,
      report_type: "team_level",
    };
    if (params.team_ids != null && params.team_ids !== "") {
      downloadPayload.team_ids =
        typeof params.team_ids === "string"
          ? params.team_ids
          : Array.isArray(params.team_ids)
            ? params.team_ids.join(",")
            : String(params.team_ids);
    }

    try {
      await triggerDepartmentTatDownload(downloadPayload).unwrap();
      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const handleTeamsChange = (event: any) => {
    const {
      target: { value },
    } = event;

    if (value.includes("all")) {
      setSelectedTeams(
        selectedTeams.length === theTeams.length
          ? []
          : theTeams.map((team) => team.id)
      );
    } else {
      // Convert the value to an array of strings
      setSelectedTeams(typeof value === "string" ? value.split(",") : value);
    }
  };

  useEffect(() => {
    setSelectedTeamsParam(selectedTeams);
  }, [selectedTeams]);

  const ITEM_HEIGHT = 60;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 150,
      },
    },
  };
  const isAllSelected = selectedTeams.length === theTeams.length;

  const gridLoading =
    reportMode === "live" ? localLoading || liveReportFetching : localLoading;
  const showSpinner =
    teamsLoading ||
    (reportMode === "live"
      ? liveReportStatus === "pending"
      : reportFetching || localLoading);

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => {
              navigate("/reports/ticket/reports-list");
            }}
          /> */}
           <button
              className="btn btn-link mb-2"
              onClick={() => {
                navigate("/reports/ticket/reports-list");
              }}
              style={{marginRight:"-17px"}}
            >
             <i className="bi bi-arrow-90deg-left"     style={{
                                        fontSize: "20px",
                                        color: "black",
                                        cursor: "pointer",
                                    }}></i>
            </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">
            {" "}
            TAT By Department
          </span>
        </div>

        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                  max: new Date().toISOString().split('T')[0],
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  style: { textTransform: "uppercase" }, // Optional: uppercase input text
                  max: new Date().toISOString().split('T')[0],
                },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <FormControl sx={{ width: "200px" }}>
              <InputLabel id="demo-multiple-checkbox-label">
                Select Teams
              </InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={selectedTeams}
                onChange={handleTeamsChange}
                input={<OutlinedInput label="Select Teams" />}
                renderValue={(selected) => {
                  if (isAllSelected) {
                    return <div>All</div>;
                  }
                  return (
                    <div>
                      {selected
                        .map((teamId) => {
                          const team = theTeams.find((t) => t.id === teamId);
                          return team ? team.name : null;
                        })
                        .join(", ")}
                    </div>
                  );
                }}
                MenuProps={MenuProps}
              >
                <MenuItem
                  value="all"
                  onClick={() =>
                    setSelectedTeams(
                      isAllSelected ? [] : theTeams.map((team) => team.id)
                    )
                  }
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedTeams?.length > 0 && !isAllSelected}
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                {theTeams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    <Checkbox checked={selectedTeams.indexOf(team.id) > -1} />
                    <ListItemText primary={team.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || dataRows.length === 0}
              >
                <CsvDownloader
                  filename="TAT By Department"
                  datas={dataRows}
                  columns={columns.map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Download
                  </>
                ) : (
                  "Download"
                )}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        <CardContent style={{ width: "100%" }}>
          {showSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={dataRows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: totalRowsFromApi ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                  whiteSpace: "nowrap", // Prevent wrapping
                  overflow: "hidden", // Hide overflowing text
                  textOverflow: "ellipsis", // Add ellipsis for overflowing text
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  whiteSpace: "nowrap", // Prevent wrapping
                  overflow: "hidden", // Hide overflowing text
                  textOverflow: "ellipsis", // Add ellipsis for overflowing text
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                }
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default DepartmentWiseTatReport;
